import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ChatroomsPanel from "./ChatroomsPanel"; // Adjust path if needed
import { rtdb, firebase, sendPushNotification, getToken } from "utils/firebase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Loader2,
  UserCircle2,
  X,
  Upload,
  ChevronRight,
  ChevronLeft,
  Video,
  Image,
  AlertCircle,
  Film,
  Paperclip,
  Send,
  Download,
} from "lucide-react";
import { format, set } from "date-fns";
import { FiPlus } from "react-icons/fi";
import axios from "axios";
import { LuUser } from "react-icons/lu";
import { BASE_URL, environment } from "utils/constant";
import { MdArrowDropDown, MdOutlineArrowDropUp } from "react-icons/md";
import CustomAudioPlayer from "components/CustomAudioPlayer";
import { fileTypeIcons } from "utils/utilityFunctions";
import { Mic, Music } from "lucide-react";
import { convertToWav, formatDuration } from "utils/audioUtils";
// Push an in-app notification to RTDB so it shows in Notifications modal
const pushInAppNotification = async ({
  title,
  message,
  snagId,
  currentUser,
}) => {
  try {
    const organization = JSON.parse(
      localStorage.getItem("organization") || "{}"
    );
    const organizationId = organization?.id;
    if (!organizationId) return;

    const siteData = JSON.parse(localStorage.getItem("siteData"));

    await rtdb.ref(`notifications-${environment}`).push({
      title,
      message,
      organization: organizationId,
      site: siteData?.id,
      date: new Date().toISOString(),
      read: false,
      type: "snag_message",
      senderId: currentUser?.user,
      snagId,
    });
  } catch (error) {
    console.error("Failed to push in-app notification:", error);
  }
};

const sendNotificationToTeam = async (data, currentUserId) => {
  // setLoading(true);
  try {
    const siteData = JSON.parse(localStorage.getItem("siteData"));
    let siteId = siteData?.id ?? 0;
    const accessToken = localStorage.getItem("access");
    var requestOptions = {
      method: "GET",
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
      redirect: "follow",
    };

    let res = await fetch(
      `${BASE_URL}/team/list-site-members/${siteId}/`,
      requestOptions
    );
    if (!res?.ok) throw new Error(res?.message ?? "Something went wrong");
    const result = await res.json();
    console.log("teams", result);
    if (!result?.status) {
      throw new Error(result?.message ?? "Something went wrong");
    }
    result?.data?.map(async (item) => {
      const member_token = await getToken(item?.user_id);
      if (member_token?.token && currentUserId !== item?.user_id) {
        console.log(
          "Sending notification to:",
          item?.user_id,
          member_token?.token
        );
        sendPushNotification(member_token?.token, data?.title, data?.body);
      }
    });
  } catch (error) {
    console.log(error);
  }
};

const MediaCarousel = ({ mediaItems, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef(null);

  const nextItem = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const prevItem = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length
    );
  };

  const isVideo = (url) => {
    if (!url) return false;
    return (
      url.toLowerCase().endsWith(".mp4") ||
      url.toLowerCase().endsWith(".mov") ||
      url.toLowerCase().endsWith(".webm") ||
      url.toLowerCase().endsWith(".avi") ||
      /\.(mp4|mov|webm|avi)/i.test(url)
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="relative max-w-4xl w-full mx-4">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="relative">
          {isVideo(mediaItems[currentIndex]) ? (
            <video
              ref={videoRef}
              src={mediaItems[currentIndex]}
              className="w-full h-auto max-h-[80vh] object-contain"
              controls
              autoPlay
            />
          ) : (
            <img
              src={mediaItems[currentIndex]}
              draggable={false}
              alt={`Media ${currentIndex + 1}`}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          )}

          {mediaItems.length > 1 && (
            <>
              <button
                onClick={prevItem}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextItem}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
            {mediaItems.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full ${currentIndex === index ? "bg-white" : "bg-white/50"
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SiteUpdateForm = ({ onClose, comment: selectedComment, imageUrls }) => {
  const [comment, setComment] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const siteId = localStorage.getItem("site");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const siteData = JSON.parse(localStorage.getItem("siteData"));

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      };

      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = '';
        }
      }

      const recorder = new MediaRecorder(stream, options);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        try {
          const mimeType = recorder.mimeType || 'audio/webm';
          const blob = new Blob(chunks, { type: mimeType });

          console.log('Original recording:', {
            size: blob.size,
            type: blob.type
          });

          // Convert to proper WAV format
          const wavBlob = await convertToWav(blob);

          console.log('Converted to WAV:', {
            size: wavBlob.size,
            type: wavBlob.type
          });

          const audioFile = new File([wavBlob], `recording-${Date.now()}.wav`, {
            type: "audio/wav",
          });

          // Check if adding this file would exceed the 10 file limit
          if (mediaFiles.length >= 10) {
            setError("Maximum 10 files allowed.");
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          // Add to media files
          setMediaFiles((prev) => [...prev, audioFile]);

          // Create preview for audio
          const previewUrl = URL.createObjectURL(wavBlob);
          setMediaPreviews((prev) => [...prev, {
            url: previewUrl,
            type: "audio",
            name: audioFile.name,
          }]);

          // Stop all tracks
          stream.getTracks().forEach((track) => track.stop());
        } catch (error) {
          console.error("Error converting audio to WAV:", error);
          setError("Failed to process recording. Please try again.");
          stream.getTracks().forEach((track) => track.stop());
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);

      // Start timer
      setRecordingDuration(0);
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setError("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingDuration(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim() && mediaFiles.length === 0) {
      setError("Comment or media is required");
      return;
    }

    setIsUploading(true);
    let mediaArray = [];

    if (mediaFiles.length > 0) {
      try {
        const accessToken = localStorage.getItem("access");
        mediaArray = await Promise.all(
          mediaFiles.map(async (file) => {
            const formData = new FormData();
            formData.append("site_id", siteId);
            formData.append("snag_id", "general-updates");

            // Determine file type
            let fileType = "document";
            if (file.type.startsWith("image/")) {
              fileType = "image";
              formData.append("image", file);
            } else if (file.type.startsWith("video/")) {
              fileType = "video";
              formData.append("file", file);
            } else if (file.type.startsWith("audio/")) {
              fileType = "audio";
              formData.append("file", file);
            } else {
              formData.append("file", file);
            }

            formData.append("type", fileType);

            const response = await fetch(
              `${BASE_URL}/core/uploadsnagobjects/`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
              }
            );

            if (response.ok) {
              const result = await response.json();
              return {
                url: result.data.image || result.data.file,
                type: fileType,
                mediaId: result.data.id,
              };
            } else {
              throw new Error(`Failed to upload ${file.name}`);
            }
          })
        );
      } catch (error) {
        setError("Error uploading media");
        console.error("Error uploading media:", error);
        setIsUploading(false);
        return;
      }
    }

    const message = {
      text: comment,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      senderId: currentUser?.user,
      senderName:
        `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      media: mediaArray.length > 0 ? mediaArray : null,
      has_media: mediaArray.length > 0,
      replyTo: selectedComment
        ? {
          id: selectedComment.id,
          text: selectedComment.text,
          senderName: selectedComment.senderName,
          media: selectedComment.media || null,
          has_media: !!selectedComment.media,
        }
        : null,
    };

    try {
      await rtdb
        .ref(`site_snags-${environment}/${siteId}/general_updates/messages`)
        .push(message);
      setComment("");
      setMediaFiles([]);
      setMediaPreviews([]);
      setIsUploading(false);
      onClose();

      const notificationData = {
        title: `New message in snag: General Updates@${siteData?.site_name}`,
        body: `${currentUser?.first_name} ${currentUser?.last_name
          }: ${comment}${mediaArray.length > 0 ? ". Media attached." : ""}`,
        data: {
          type: "snag_message",
          snagId: "general_updates",
          siteId: siteData.id,
          senderId: currentUser?.user,
        },
      };

      // Note: Add your notification functions here
      await sendNotificationToTeam(notificationData, currentUser?.user);
      await pushInAppNotification({
        title: notificationData.title,
        message: notificationData.body,
        snagId: "general_updates",
        currentUser
      });

    } catch (error) {
      setError("Error posting update");
      console.error("Error posting update:", error);
      setIsUploading(false);
    }
  };

  const isVideo = (file) => file.type.startsWith("video/");
  const isImage = (file) => file.type.startsWith("image/");
  const isAudio = (file) => file.type.startsWith("audio/");

  const validateFile = (file) => {
    const videoSizeLimit = 20 * 1024 * 1024; // 20MB
    const imageSizeLimit = 15 * 1024 * 1024; // 15MB
    const audioSizeLimit = 10 * 1024 * 1024; // 10MB

    if (isVideo(file) && file.size > videoSizeLimit) {
      return `Video size exceeds 20MB limit (${(
        file.size /
        (1024 * 1024)
      ).toFixed(2)}MB)`;
    }
    if (isImage(file) && file.size > imageSizeLimit) {
      return `Image size exceeds 15MB limit (${(
        file.size /
        (1024 * 1024)
      ).toFixed(2)}MB)`;
    }
    if (isAudio(file) && file.size > audioSizeLimit) {
      return `Audio size exceeds 10MB limit (${(
        file.size /
        (1024 * 1024)
      ).toFixed(2)}MB)`;
    }
    return null;
  };

  const handleMediaUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const totalFilesAfterUpload = mediaFiles.length + files.length;
    if (totalFilesAfterUpload > 10) {
      const remainingSlots = 10 - mediaFiles.length;
      setError(
        `Maximum 10 files allowed. You can only add ${remainingSlots} more file${remainingSlots !== 1 ? "s" : ""
        }.`
      );
      return;
    }

    const validFiles = [];
    const previews = [];

    files.forEach((file) => {
      // if (!isImage(file) && !isVideo(file) && !isAudio(file)) {
      //   setError("Only image, video, and audio files are allowed");
      //   return;
      // }

      const sizeError = validateFile(file);
      if (sizeError) {
        setError(sizeError);
        return;
      }

      validFiles.push(file);
      const previewUrl = URL.createObjectURL(file);

      let previewType = "file";
      if (isImage(file)) previewType = "image";
      if (isVideo(file)) previewType = "video";
      if (isAudio(file)) previewType = "audio";

      previews.push({
        url: previewUrl,
        type: previewType,
        name: file.name,
      });
    });

    if (validFiles.length > 0) {
      setMediaFiles((prev) => [...prev, ...validFiles]);
      setMediaPreviews((prev) => [...prev, ...previews]);
      setError("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeMedia = (index) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    setMediaPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Post General Update</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {selectedComment && (
            <>
              <div className="flex items-center gap-2 font-bold text-gray-900">
                <LuUser className="w-4 h-4 text-gray-600" />
                {selectedComment.senderName || "Unknown"}
              </div>
              <p className="text-sm text-gray-700">{selectedComment.text}</p>
              {selectedComment.has_media && selectedComment.media && (
                <div className="flex gap-2 flex-wrap">
                  {(Array.isArray(selectedComment.media)
                    ? selectedComment.media
                    : [selectedComment.media]
                  ).map((media, index) => (
                    <div key={index} className="relative">
                      {media.type === "image" ? (
                        <img
                          src={imageUrls[media.mediaId] || media.url}
                          alt={`Media ${index + 1}`}
                          className="w-20 h-20 object-cover rounded"
                        />
                      ) : (
                        <video
                          src={imageUrls[media.mediaId] || media.url}
                          className="w-20 h-20 object-cover rounded"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="text-xs text-gray-400">
                {format(new Date(selectedComment.createdAt), "hh:mm a, dd MMM")}
              </div>
            </>
          )}

          <textarea
            className="w-full border rounded-lg p-2 text-sm"
            placeholder="Reply to this update"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            disabled={isRecording}
          />

          {/* Recording indicator */}
          {isRecording && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm text-red-700 font-medium">
                Recording... {formatDuration(recordingDuration)}
              </span>
            </div>
          )}

          {mediaPreviews.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {mediaPreviews.map((preview, index) => (
                <div key={index} className="relative">
                  {preview.type === "video" ? (
                    <video
                      src={preview.url}
                      className="w-20 h-20 object-cover rounded"
                    />
                  ) : preview.type === "audio" ? (
                    <div className="w-20 h-20 bg-purple-100 rounded flex flex-col items-center justify-center">
                      <Music className="w-8 h-8 text-purple-600" />
                      <span className="text-xs text-purple-600 mt-1">Audio</span>
                    </div>
                  ) : preview.type === "file" ? (
                    <div className="w-20 h-20 bg-gray-100 rounded flex flex-col items-center justify-center">
                      {fileTypeIcons({ fileType: preview.type, className: "w-8 h-8 text-gray-600" })}
                      <span className="text-xs text-gray-600 mt-1">File</span>
                    </div>
                  ) : (
                    <img
                      src={preview.url}
                      alt={`Preview ${index + 1}`}
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeMedia(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-red-500 text-sm flex items-center gap-1">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                disabled={isUploading || mediaFiles.length >= 10 || isRecording}
                title="Attach file"
              >
                <Paperclip size={20} />
              </button>

              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                className={`p-2 rounded-full transition-colors ${isRecording
                  ? "bg-red-100 text-red-600 hover:bg-red-200"
                  : "text-gray-500 hover:bg-gray-100"
                  }`}
                disabled={isUploading || mediaFiles.length >= 10}
                title={isRecording ? "Stop recording" : "Start recording"}
              >
                <Mic size={20} />
              </button>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleMediaUpload}
              accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              multiple
              className="hidden"
            />

            <button
              type="submit"
              className="px-4 py-2 bg-purple-700 text-white rounded hover:bg-purple-800 disabled:bg-gray-400"
              disabled={isUploading || isRecording}
            >
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Post"
              )}
            </button>
          </div>

          {mediaFiles.length > 0 && (
            <div className="text-xs text-gray-500 text-right">
              {mediaFiles.length}/10 files selected
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const formatDateSeparator = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const yesterdayDate = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate()
  );

  if (messageDate.getTime() === todayDate.getTime()) {
    return "Today";
  } else if (messageDate.getTime() === yesterdayDate.getTime()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
};

const SiteCommunications = ({ setTab, setSnagId }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showUpdates, setShowUpdates] = useState(true);
  const [snags, setSnags] = useState([]);
  const [activeSnag, setActiveSnag] = useState(null);
  const [showChatrooms, setShowChatrooms] = useState(true);
  const [showNewSnagForm, setShowNewSnagForm] = useState(false);
  const [newSnagTitle, setNewSnagTitle] = useState("");
  const [generalMessages, setGeneralMessages] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastMessages, setLastMessages] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const siteId = localStorage.getItem("site");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isOwnerOrAdmin = teamMembers.some(
    (m) =>
      m.user_id === currentUser?.user && (m.joined_as === "owner" || m.is_admin)
  );

  const getImageUrl = async (mediaId) => {
    if (!mediaId) return null;
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/core/api/snag/${mediaId}/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        return data.data; // Assuming the API returns an image URL
      } else {
        console.error("Failed to fetch media URL:", response.statusText);
        return null;
      }
    } catch (error) {
      console.error("Error fetching media URL:", error);
      return null;
    }
  };

  const useImageUrl = (mediaId) => {
    return useQuery({
      queryKey: ["imageUrl", mediaId],
      queryFn: () => getImageUrl(mediaId),
      enabled: !!mediaId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
    });
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/team/list-site-members/${siteId}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTeamMembers(data.data || []);
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
      }
    };

    if (siteId) {
      fetchTeamMembers();
    }
  }, [siteId]);

  // Fetch snags and general updates from Firebase
  useEffect(() => {
    if (!siteId) return;

    let newImageUrls = {};

    // Fetch snags
    const snagsRef = rtdb.ref(`site_snags-${environment}/${siteId}`);
    snagsRef.on("value", (snapshot) => {
      const snagsData = [];
      snapshot.forEach((child) => {
        const key = child.key;
        if (key !== "general_updates") {
          snagsData.push({
            id: key,
            ...child.val(),
          });
        }
      });
      setSnags(snagsData.sort((a, b) => b.createdAt - a.createdAt));
      if (!activeSnag && snagsData.length > 0) {
        setActiveSnag(snagsData[0].id);
      }
      const visibleSnags = snagsData.filter((snag) => {
        if (isOwnerOrAdmin) return true; // see everything
        const members = snag?.members || [];
        return members.length === 0 || members.includes(currentUser?.user);
      });
      setSnags(visibleSnags.sort((a, b) => b.createdAt - a.createdAt));
      if (!activeSnag && visibleSnags.length > 0) {
        setActiveSnag(visibleSnags[0].id);
      }
    });

    // Fetch general updates
    const generalMessagesRef = rtdb.ref(
      `site_snags-${environment}/${siteId}/general_updates/messages`
    );
    generalMessagesRef
      .orderByChild("createdAt")
      .on("value", async (snapshot) => {
        const msgs = [];
        snapshot.forEach((childSnapshot) => {
          msgs.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        // for (const msg of msgs) {
        //   if (
        //     msg.has_media &&
        //     msg.media &&
        //     msg.media.mediaId &&
        //     !Array.isArray(msg.media)
        //   ) {
        //     if (!newImageUrls[msg.id]) {
        //       const url = await getImageUrl(msg.media.mediaId);
        //       if (url?.image) {
        //         newImageUrls[msg.media.mediaId] = url?.image;
        //       }
        //     }
        //   } else if (Array.isArray(msg.media)) {
        //     for (const media of msg.media) {
        //       if (media.mediaId && !newImageUrls[media.mediaId]) {
        //         const url = await getImageUrl(media.mediaId);
        //         if (url?.image) {
        //           newImageUrls[media.mediaId] = url?.image;
        //         }
        //       }
        //     }
        //   }
        // }
        // setImageUrls((prev) => ({ ...prev, ...newImageUrls }));
        setGeneralMessages(msgs);
      });

    return () => {
      snagsRef.off();
      generalMessagesRef.off();
    };
  }, [siteId, activeSnag]);

  // Calculate unread counts and last messages
  useEffect(() => {
    if (!siteId || !currentUser?.user) return;

    const unreadCountsObj = {};
    const lastMessagesObj = {};

    // Monitor general updates
    const generalReadRef = rtdb.ref(
      `site_snags-${environment}/${siteId}/general_updates/read_by`
    );
    generalReadRef.on("value", (readSnapshot) => {
      const readData = readSnapshot.val() || {};
      unreadCountsObj["general-updates"] = calculateUnreadCount(
        generalMessages,
        readData
      );

      if (generalMessages.length > 0) {
        const lastMsg = generalMessages[generalMessages.length - 1];
        lastMessagesObj["general-updates"] = {
          text: lastMsg.text || "Media",
          senderName: lastMsg.senderName,
          createdAt: lastMsg.createdAt,
        };
      }

      setUnreadCounts({ ...unreadCountsObj });
      setLastMessages({ ...lastMessagesObj });
    });

    // Monitor snags
    snags.forEach((snag) => {
      const snagMessagesRef = rtdb.ref(
        `site_snags-${environment}/${siteId}/${snag.id}/messages`
      );
      const snagReadRef = rtdb.ref(
        `site_snags-${environment}/${siteId}/${snag.id}/read_by`
      );

      snagMessagesRef.orderByChild("createdAt").on("value", (msgSnapshot) => {
        const msgs = [];
        msgSnapshot.forEach((childSnapshot) => {
          msgs.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });

        snagReadRef.on("value", (readSnapshot) => {
          const readData = readSnapshot.val() || {};
          unreadCountsObj[snag.id] = calculateUnreadCount(msgs, readData);

          if (msgs.length > 0) {
            const lastMsg = msgs[msgs.length - 1];
            lastMessagesObj[snag.id] = {
              text: lastMsg.text || "Media",
              senderName: lastMsg.senderName,
              createdAt: lastMsg.createdAt,
            };
          }

          setUnreadCounts({ ...unreadCountsObj });
          setLastMessages({ ...lastMessagesObj });
        });
      });
    });

    return () => {
      generalReadRef.off();
      snags.forEach((snag) => {
        rtdb
          .ref(`site_snags-${environment}/${siteId}/${snag.id}/messages`)
          .off();
        rtdb
          .ref(`site_snags-${environment}/${siteId}/${snag.id}/read_by`)
          .off();
      });
    };
  }, [siteId, currentUser?.user, snags, generalMessages]);

  // Mark messages as read when clicking General Updates
  const markMessagesAsRead = async (chatId, isGeneral = false) => {
    if (!currentUser?.user) return;

    const readPath = isGeneral
      ? `site_snags-${environment}/${siteId}/general_updates/read_by/${currentUser.user}`
      : `site_snags-${environment}/${siteId}/${chatId}/read_by/${currentUser.user}`;

    try {
      await rtdb.ref(readPath).set({
        lastReadAt: firebase.database.ServerValue.TIMESTAMP,
        userId: currentUser.user,
      });
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const calculateUnreadCount = (messages, readData) => {
    if (!readData || !currentUser?.user) return messages.length;

    const userReadData = readData[currentUser.user];
    if (!userReadData || !userReadData.lastReadAt) return messages.length;

    return messages.filter(
      (msg) =>
        msg.createdAt > userReadData.lastReadAt &&
        msg.senderId !== currentUser.user
    ).length;
  };

  const handleCreateSnag = async () => {
    if (!newSnagTitle.trim()) return;

    const newSnag = {
      title: newSnagTitle,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      createdBy: currentUser?.user || "unknown",
      creatorName:
        `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      messages: {},
    };

    try {
      const snagsRef = rtdb.ref(`site_snags-${environment}/${siteId}`);
      const newSnagRef = await snagsRef.push(newSnag);
      setActiveSnag(newSnagRef.key);
      setNewSnagTitle("");
      setShowNewSnagForm(false);
      setActiveSnag(newSnagRef.key);
      setTab("Snags");
      setSnagId(newSnagRef.key);
    } catch (error) {
      console.error("Error creating snag:", error);
    }
  };

  const isVideoUrl = (url) => {
    if (!url) return false;
    return (
      url.toLowerCase().endsWith(".mp4") ||
      url.toLowerCase().endsWith(".mov") ||
      url.toLowerCase().endsWith(".webm") ||
      url.toLowerCase().endsWith(".avi") ||
      /\.(mp4|mov|webm|avi)/i.test(url)
    );
  };
  const isImageUrl = (url) => {
    if (!url) return false;
    return (
      url.toLowerCase().endsWith(".jpg") ||
      url.toLowerCase().endsWith(".jpeg") ||
      url.toLowerCase().endsWith(".png") ||
      url.toLowerCase().endsWith(".gif") ||
      url.toLowerCase().endsWith(".webp") ||
      /\.(jpg|jpeg|png|gif|webp)/i.test(url)
    );
  };
  const MediaRenderer = ({ media, isArray = false }) => {
    if (isArray) {
      return (
        <div className="flex flex-wrap gap-2 mb-2">
          {media.map((mediaItem) => (
            <MediaItem key={mediaItem.mediaId} mediaItem={mediaItem} />
          ))}
        </div>
      );
    } else {
      return (
        <div className="mb-2">
          <MediaItem mediaItem={media} />
        </div>
      );
    }
  };

  // Individual media item component with React Query
  const MediaItem = ({ mediaItem }) => {
    const { data: mediaData, isLoading, error } = useImageUrl(mediaItem.mediaId);

    if (isLoading) {
      return (
        <div className="h-20 w-20 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      );
    }

    if (error || !mediaData) {
      return (
        <div className="h-20 w-20 flex items-center justify-center bg-gray-200 rounded">
          <X className="w-6 h-6 text-gray-400" />
        </div>
      );
    }

    const url = mediaData.image || mediaData.file;
    let type = mediaData.type;
    if (isVideoUrl(url)) {
      type = "video";
    }
    else if (isImageUrl(url)) {
      type = "image";
    }

    if (type === "audio") {
      return (
        <CustomAudioPlayer url={url} transcription={mediaData.transcription} />
      );
    }

    if (type === "video" || isVideoUrl(url)) {
      return (
        <video
          src={url}
          controls
          className="rounded h-20 w-20 cursor-pointer object-cover bg-black"
        />
      );
    }

    if (type === "image" || (!type && url)) {
      return (
        <img
          src={url}
          alt="Shared media"
          className="rounded h-20 w-20 cursor-pointer object-cover border border-gray-200"
          onClick={() => {
            setSelectedMedia(url);
          }}
        />
      );
    }

    // Default for documents/PDFs/other
    const fileName = url ? url.split('?')[0].split('/').pop() : "Document";
    const extension = fileName.split(".").pop().toLowerCase();
    const isViewable = ["pdf", "txt", "docx", "doc"].includes(extension);

    return (
      <div className="flex items-center gap-2">
        <div
          onClick={() => {
            // if (isViewable) {
            //   setViewingFile({ url, name: fileName });
            // }
          }}
          className={`flex items-center gap-2 p-2 bg-gray-100 rounded border border-gray-200 max-w-[200px] ${isViewable ? "cursor-pointer hover:bg-gray-200" : ""
            } transition-colors`}
        >
          {/* <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" /> */}
          {fileTypeIcons({ fileType: extension, className: "w-5 h-5 text-purple-600 flex-shrink-0" })}
          <span className="text-xs text-gray-700 truncate" title={fileName}>
            {fileName}
          </span>
        </div>
        <a
          href={url}
          download
          className="p-2 bg-gray-100 rounded border border-gray-200 hover:bg-gray-200 text-gray-600"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </a>
      </div>
    );
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [generalMessages]);

  return (
    <div className="h-[86vh] w-full flex flex-col ">
      {isFormOpen && (
        <SiteUpdateForm
          onClose={() => {
            setIsFormOpen(false);
            setSelectedComment(null);
          }}
          imageUrls={imageUrls}
          comment={selectedComment}
          onSubmit={() => { }} // Empty function since Firebase handles submission
        />
      )}

      {selectedMedia && (
        <MediaCarousel
          mediaItems={[selectedMedia]}
          onClose={() => setSelectedMedia(null)}
        />
      )}

      <div
        className="flex-1 p-2 w-full z-10 flex flex-col bg-white rounded-lg border-[1px] border-[#CCCCCC]"
        style={{ gap: "0.5rem", overflowY: "auto", scrollbarWidth: "none" }}
      >
        <div
          className="flex-1 h-[55%] pb-2 flex flex-col border-b-[1px] border-gray-300"
          style={{
            scrollbarWidth: "none",
          }}
        >
          <div className="sticky top-0 z-10 bg-white flex items-center justify-between pr-3 pl-3 py-2">
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => {
                setShowUpdates(!showUpdates);
                markMessagesAsRead("general-updates", true);
                setSnagId("general-updates");
                setTab("Snags");
              }}
            >
              {showUpdates ? (
                <MdOutlineArrowDropUp className="h-5 w-5 text-[#8D8D8D]" />
              ) : (
                <MdArrowDropDown className="h-5 w-5 text-[#8D8D8D]" />
              )}
              <h3 className="font-bold text-purple-900">General Updates</h3>
              {unreadCounts["general-updates"] > 0 && (
                <div className="bg-purple-900 text-white text-xs px-2 py-1 font-medium rounded-full min-w-[24px] h-6 flex items-center justify-center ml-2">
                  {unreadCounts["general-updates"]}
                </div>
              )}
            </div>
            <div className="flex-grow" />
            <div className="flex items-center gap-3">
              {/* <span className="text-sm text-gray-500">Today-Last 7 Days</span> */}
              <button
                onClick={() => setIsFormOpen(true)}
                className="flex items-center justify-center p-1 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200"
              >
                <FiPlus className="text-text-clr w-5 h-5 font-bold" />
              </button>
            </div>
          </div>
          {showUpdates && (
            <div className="flex-1 overflow-auto p-2">
              {generalMessages.length === 0 ? (
                <div className="text-center p-8 text-gray-500">
                  No site updates yet today.
                </div>
              ) : (
                <div className="space-y-2">
                  {generalMessages.map((msg, index) => (
                    <div
                      key={msg.id}
                      className={`p-2 mx-auto w-full rounded-xl ${index !== generalMessages.length - 1
                        ? "bg-purple-50 border-b"
                        : ""
                        } border-[#00000033] hover:bg-purple-100 transition-colors`}
                    >
                      <div className="flex flex-row items-start gap-2">
                        <div className="p-2 rounded-full bg-[#E5ECF6] flex items-center justify-center">
                          <LuUser className="w-5 h-4 text-black" />
                        </div>
                        <div className="flex-col gap-0 items-start">
                          <p className="text-xs font-semi-bold">
                            {msg.senderName}
                          </p>
                          {/* <p className="text-[14px] text-gray-600 break-words">
                            {msg.text}
                          </p> */}
                          {msg.replyTo && (
                            <div className="mb-2 p-2 rounded-md border-l-4 border-purple-300 bg-purple-50 text-gray-700 text-sm">
                              <div className="font-medium text-purple-800">
                                {msg.replyTo.senderName}
                              </div>
                              <div className="text-sm text-gray-400 line-clamp-2 break-words">
                                {msg.replyTo.text || "Media"}
                              </div>
                              {/* {msg.replyTo.media?.url && msg.replyTo.media?.type === "image" && (
                                <img
                                  src={msg.replyTo.media.url}
                                  alt="Replied Media"
                                  className="mt-1 w-16 h-10 object-cover rounded"
                                />
                              )} */}
                              {/* {msg.replyTo.media?.url &&
                                msg.replyTo.media?.type === "video" && (
                                  <video
                                    src={msg.replyTo.media.url}
                                    className="mt-1 w-16 h-10 object-cover rounded"
                                  />
                                )} */}
                            </div>
                          )}

                          <p className="text-[14px] w-[16vw] text-gray-800 break-words">
                            {msg.text}
                          </p>

                          {msg.media && !Array.isArray(msg.media) && (
                            <div
                              className="flex gap-2 mt-2 overflow-x-auto cursor-pointer"
                            // onClick={() =>
                            //   setSelectedMedia(imageUrls[msg.media.mediaId])
                            // }
                            >
                              {/* {msg.media.type === "image" ? (
                                <img
                                  src={imageUrls[msg.media.mediaId]}
                                  alt="Media"
                                  className="w-16 h-10 border-[0px] shadow-inner shadow-slate-200 object-cover rounded-lg"
                                />
                              ) : (
                                <div className="relative w-16 h-10">
                                  <video
                                    src={imageUrls[msg.media.mediaId]}
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                  <div className="absolute top-1 left-1 p-1 bg-black bg-opacity-50 rounded-full">
                                    <Film className="w-3 h-3 text-white" />
                                  </div>
                                </div>
                              )} */}
                              <MediaRenderer media={msg.media} />
                            </div>
                          )}
                          {msg.media && Array.isArray(msg.media) && (
                            // <div className="flex gap-2 mt-2 flex-wrap cursor-pointer">
                            //   {msg.media.map((media, idx) => (
                            //     <div
                            //       key={idx}
                            //       className="relative w-16 h-10"
                            //       onClick={() =>
                            //         setSelectedMedia(imageUrls[media.mediaId])
                            //       }
                            //     >
                            //       {media.type === "image" ? (
                            //         <img
                            //           src={imageUrls[media.mediaId]}
                            //           alt={`Media ${idx + 1}`}
                            //           className="w-full h-full object-cover rounded-lg"
                            //         />
                            //       ) : (
                            //         <video
                            //           src={imageUrls[media.mediaId]}
                            //           className="w-full h-full object-cover rounded-lg"
                            //         />
                            //       )}
                            //       {media.type === "video" && (
                            //         <div className="absolute top-1 left-1 p-1 bg-black bg-opacity-50 rounded-full">
                            //           <Film className="w-3 h-3 text-white" />
                            //         </div>
                            //       )}
                            //     </div>
                            //   ))}
                            // </div>
                            <MediaRenderer media={msg.media} isArray={true} />
                          )}
                          <div className="mt-2 flex items-center justify-between w-full">
                            <button
                              onClick={() => {
                                setSelectedComment(msg);
                                setIsFormOpen(true);
                              }}
                            >
                              <div className="text-sm text-purple-900 hover:font-bold">
                                Reply
                              </div>
                            </button>
                            <div className="text-xs text-gray-400 ml-8">
                              {format(
                                new Date(msg.createdAt),
                                "hh:mm a, dd MMM"
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />{" "}
                  {/* Marker for scrolling to bottom */}
                </div>
              )}
            </div>
          )}
        </div>


        <div
          className="flex-1 h-[45%]"
          style={{ overflowY: "auto", scrollbarWidth: "none" }}
        >
          <ChatroomsPanel
            showChatrooms={showChatrooms}
            setShowChatrooms={setShowChatrooms}
            showNewSnagForm={showNewSnagForm}
            setShowNewSnagForm={setShowNewSnagForm}
            snags={snags}
            setTab={setTab}
            activeSnag={activeSnag}
            setActiveSnag={setActiveSnag}
            unreadCounts={unreadCounts}
            lastMessages={lastMessages}
            onCreateSnag={handleCreateSnag}
            newSnagTitle={newSnagTitle}
            setSnagId={setSnagId}
            setNewSnagTitle={setNewSnagTitle}
            isAdmin={isOwnerOrAdmin}
          />
        </div>
      </div>
    </div>
  );
};

export default SiteCommunications;